import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary CTA - orange accent for main actions
        default: "bg-accent text-accent-foreground hover:bg-accent/85",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // Clean outline style
        outline: "border border-border bg-background text-foreground hover:bg-muted hover:border-foreground/20",
        // Secondary CTA - brand blue
        secondary: "bg-primary text-primary-foreground hover:bg-primary/90",
        ghost: "hover:bg-muted hover:text-foreground",
        link: "text-foreground underline-offset-4 hover:underline",
        // Hero buttons - orange CTA
        hero: "bg-accent text-accent-foreground hover:bg-accent/85",
        "hero-outline": "border border-foreground/20 bg-transparent text-foreground hover:bg-foreground hover:text-background",
      },
      size: {
        default: "h-10 px-5 py-2 text-sm",
        sm: "h-9 rounded-md px-4 text-sm",
        lg: "h-11 rounded-md px-6 text-sm font-medium",
        xl: "h-12 rounded-md px-8 text-base font-medium",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
